/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Divider, Message, Menu, MenuItem } from "semantic-ui-react";
import DeliveryUpload from "components/DeliveryUpload/DeliveryUpload";
import CampaignForm from "forms/Campaign";
import DeliveryForm from "forms/Delivery";
import CampaignDisplayOriginalAssets from "../CampaignDisplayOriginalAsset/CampaignDisplayOriginalAssets";
import CampaignDisplayProcessedAssets from "../CampaignDisplayProcessedAssets/CampaignDisplayProcessedAssets";
import {
  getActiveUser,
  getGroupedCampaignById,
  getProcessPollingStatus,
  getAgencys,
} from "store/selectors";
import CampaignInfo, {
  getGroupedActiveNativeResolutions,
} from "components/CampaignInfo/CampaignInfo";
import _ from "lodash";
import {
  getZippedAssets,
  sendAssetsToPrint,
  sendAssetsToDigitalService,
  getCurrentAssetsStatusForView,
  invokeAssetSFNProcessing,
  pollProcessAssetsStatus,
} from "services/apiMiddleware";
import { CampaignType, CampaignStatus } from "models";
import { toast } from "../Toast/Toast";
import LoadResolver from "../LoadResolver";
import {
  downloadFromUrl,
  getAWSDate,
  today,
  isShownInProcessedAssets,
  getCountryCode,
} from "services/common";
import { ACTIVE_CAMPAIGN_STATUSES } from "constants/campaignStatuses";
import ProcessConfirmationModal from "../ProcessConfirmationModal/ProcessConfirmationModal";

const CampaignDisplay = ({ campaignId, users, dispatch, activeUser, isAdmin }) => {
  const [campaignHasBeenSearched, setCampaignHasBeenSearched] = useState(false);
  const [currentAssets, setCurrentAssets] = useState([]);
  const agencys = useSelector(getAgencys);

  useEffect(() => {
    dispatch.campaigns.loadOne(campaignId);
    dispatch.approvers.load();
    dispatch.assets.loadCampaignAssets(campaignId);
    dispatch.agencys.load();
    setCampaignHasBeenSearched(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { campaign, assets, processedAssets, isLoading } =
    useSelector((state) => getGroupedCampaignById(state, { campaignId })) || {};

  const isPolling = useSelector(getProcessPollingStatus);

  const originalAssets = assets ? assets.filter((asset) => asset.campaignID === campaign.id) : [];

  useEffect(() => {
    // With fetching all campaign assets directly we feed data to functions which check if required resolutions are delivered
    // even though the user with contributor role has no access to assets which the user is not in an owner position
    const getCurrentAssetStatus = async () => {
      const { errorMessage, response } = await getCurrentAssetsStatusForView(campaignId);
      if (errorMessage) {
        toast("error", errorMessage);
      } else {
        setCurrentAssets(response);
      }
    };

    if (campaign && campaign.type === CampaignType.DIGITAL) {
      getCurrentAssetStatus();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  useEffect(() => {
    if (isLoading) {
      dispatch.campaigns.loadOne(campaignId);
      dispatch.assets.loadCampaignAssets(campaignId);
    }
  }, []);

  const { t } = useTranslation("common");
  const [editingCampaign, setEditingCampaign] = useState(false);
  const [processedAssetsViewOpen, setProcessedAssetsViewOpen] = useState(false);
  const [editingAssets, setEditingAssets] = useState(false);
  const [assetActions, setAssetActions] = useState();
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedPrintServiceID, setSelectedPrintServiceID] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingAssets, setIsDownloadingAssets] = useState(false);
  const [isProcessingAssets, setIsProcessingAssets] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    text: "",
    headerText: "",
    list: [],
    icon: "",
  });
  const [confirmationModalDataForOnSuccess, setConfirmationModalDataForOnSuccess] = useState([]);
  // Resolutions for both types mp4 and jpeg
  const deliveredAssetResolutions = useMemo(
    () =>
      campaign && campaign.type === CampaignType.DIGITAL ? _.map(currentAssets, "resolution") : [],
    [campaign, currentAssets]
  );

  const processedAssetsList = useMemo(() => {
    const filteredProcessedAssets = assets
      ? assets.filter((asset) => isShownInProcessedAssets(asset, processedAssets))
      : [];
    return filteredProcessedAssets;
  }, [assets, processedAssets]);
  // Only resolutions for jpeg assets
  const deliveredImageAssetResolutions = useMemo(
    () =>
      campaign && campaign.type === CampaignType.DIGITAL
        ? _.map(
            _.filter(currentAssets, (asset) => _.includes(["image/jpeg", "image/png"], asset.type)),
            "resolution"
          )
        : [],
    [campaign, currentAssets]
  );

  const user = useSelector(getActiveUser);

  const onDeliverySuccess = (ac) => {
    setAssetActions(ac);
  };

  const onAssetSelected = ({ asset, isChecked }) => {
    const newSelectedAssets = isChecked
      ? _.concat(selectedAssets, asset)
      : _.without(selectedAssets, asset);

    setSelectedAssets(newSelectedAssets);
  };
  const serviceOptions = useMemo(
    () => (type) =>
      !agencys.isLoading && agencys.items
        ? [
            ...(type === "PRINT_SERVICE" &&
            getCountryCode() === "pl" && // Add the StartPrint API option only for Print Services in the Poland domain
            activeUser.isSuperAdmin // and if the user is superadmin
              ? [
                  {
                    key: "StartPrint", // API option to send assets to StartPrint API
                    value: "StartPrint",
                    text: "StartPrint API",
                  },
                ]
              : []),
            ...agencys.items
              .filter((agency) => agency.type === type)
              .map((agency) => ({
                key: agency.id,
                value: agency.id,
                text: agency.name,
              })),
          ]
        : [],
    [agencys]
  );

  const submitSendAssets = async () => {
    setIsSubmitting(true);
    const printService = _.find(agencys.items, ["id", selectedPrintServiceID]);
    const { errorMessage } = await sendAssetsToPrint(selectedAssets, printService, user.item);

    if (errorMessage) {
      toast("error", errorMessage);
    } else {
      // After successfully sending (email to 'printhouse') update campaign status => SENT_TO_PRINT
      await dispatch.campaigns.updateStatus({
        id: campaign.id,
        status: CampaignStatus.SENT_TO_PRINT,
      });
      toast(
        undefined,
        t("campaign_display_asset_sent_successful", { printServiceName: printService.name })
      );
      // Load updated asset(s)
      dispatch.assets.loadCampaignAssets(campaignId);
    }
    setIsSubmitting(false);
  };

  const submitDigitalAssets = async (selectedAssets, digitalServices) => {
    setIsSubmitting(true);
    const selectecAssetsIds = selectedAssets.map((asset) => asset.id);
    const { errorMessage } = await sendAssetsToDigitalService(
      selectecAssetsIds,
      digitalServices,
      user.item,
      campaign.scheduling_notes
    );

    if (errorMessage) {
      toast("error", errorMessage);
    } else {
      toast(undefined, t("campaign_display_asset_sent_successful_digital"));
      dispatch.assets.loadCampaignAssets(campaignId);
    }
    setIsSubmitting(false);
  };

  const downloadAssets = async (assets) => {
    if (!assets) return;

    try {
      // Remove ARRIVED assets
      const validAssets = assets.filter((asset) => asset.assetStatus !== "ARRIVED");

      // If nothing left to download - show error and stop
      if (validAssets.length === 0) {
        toast("error", t("campaign_display_assets_download_error"));
        return;
      }

      let downloadUrl;

      if (validAssets.length > 1) {
        setIsDownloadingAssets(true);
        const assetMediaArr = _.map(validAssets, "media");
        downloadUrl = await getZippedAssets(assetMediaArr, campaignId);
      } else {
        downloadUrl = validAssets[0].mediaUrl;
      }

      downloadFromUrl(downloadUrl);
    } catch (error) {
      toast("error", t("campaign_display_assets_download_error"));
    }

    setIsDownloadingAssets(false);
  };

  const buildAssetWithResolution = (assets) => {
    if (campaign) {
      const groupedResolutions = getGroupedActiveNativeResolutions(
        campaign.requiredResolutions.items,
        campaign.nativeResolutions.items
      );

      const temporaryGroup = groupedResolutions.map((item) => ({
        ...item,
        reqResolution: item.requiredResolution.requiredResolution.value,
      }));

      const results = [];

      assets?.map((item) =>
        temporaryGroup.map((tempItem) => {
          if (item.resolution === tempItem.reqResolution) {
            results.push({ ...item, resolutions: tempItem });
          }
          return null;
        })
      );

      return results;
    }
  };

  const stageProcessedAssets = (assets) => {
    const allowedAssetStatuses = [
      "QC_OK",
      "QC_CAUTION",
      "APPROVED",
      "PENDING",
      "REJECTED",
      "PARTIALLY_REJECTED",
    ];
    setConfirmationModalData({
      list: buildAssetWithResolution(assets).filter((item) =>
        allowedAssetStatuses.includes(item.status)
      ),
      errorList: assets.filter(
        (item) =>
          item.metadata.qc_resolution_error === "qc_not_campaign_size_error" ||
          item.metadata.qc_result === "qc_size_does_not_exist_error" ||
          item.status === "QC_FAILED" ||
          item.metadata.qc_resolution_error === "qc_size_does_not_exist_error" ||
          item.type.includes("zip")
      ),
      icon: "",
    });
    setConfirmationModalDataForOnSuccess(
      buildAssetWithResolution(assets).filter((item) => allowedAssetStatuses.includes(item.status))
    );
    setConfirmationModalOpen(true);
  };

  const POLL_INTERVAL_MS = 3000; // poll every 3s

  const sendAssetsToProcessing = async (data) => {
    setIsProcessingAssets(true);

    const { executionArn, errorMessage } = await invokeAssetSFNProcessing(data);

    if (errorMessage) {
      toast("error", errorMessage);
      setIsProcessingAssets(false);
      return;
    }

    const pollStatus = async () => {
      try {
        const { status, errorMessage } = await pollProcessAssetsStatus(executionArn);
        if (errorMessage) {
          toast("error", errorMessage);
          setIsProcessingAssets(false);
          return;
        }
        // DEBUGGING
        console.log(status);
        if (status === "RUNNING") {
          // keep polling
          setTimeout(pollStatus, POLL_INTERVAL_MS);
        } else if (status === "SUCCEEDED") {
          setIsProcessingAssets(false);
          toast(undefined, "Assets processed successfully");
          dispatch.assets.loadCampaignAssets(campaignId);
          setSelectedAssets([]);
        } else {
          setIsProcessingAssets(false);
          toast("error", "Processing failed");
        }
      } catch (err) {
        console.error("Polling error:", err);
        setIsProcessingAssets(false);
        toast("error", "Could not check execution status");
      }
    };
    pollStatus();
  };

  const isCampaignExpired = useMemo(
    () => (campaign && campaign.end_time ? campaign.end_time < getAWSDate(today) : false),
    [campaign]
  );

  const isCampaignActive = useMemo(
    () => campaign && _.includes(ACTIVE_CAMPAIGN_STATUSES, campaign.status),
    [campaign]
  );
  const isOwner = campaign && activeUser && activeUser.username === campaign.owner;
  if (!editingCampaign && !editingAssets)
    return (
      <LoadResolver isLoading={isLoading || agencys.isLoading} hideWhenLoading>
        {campaignHasBeenSearched && campaign ? (
          <>
            <CampaignInfo
              campaign={campaign}
              onEdit={() => setEditingCampaign(true)}
              deliveredAssetResolutions={deliveredAssetResolutions}
              deliveredImageAssetResolutions={deliveredImageAssetResolutions}
              isCampaignActive={isCampaignActive}
              isAdmin={isAdmin}
              activeUser={activeUser}
              isOwner={isOwner}
            />
            <Divider />
            {isAdmin && campaign?.type === CampaignType.DIGITAL && processedAssetsList.length > 0 && (
              <Menu
                secondary
                size="small"
                className="app-bar"
                pointing
                style={{ maxWidth: "700px" }}
              >
                <MenuItem
                  active={!processedAssetsViewOpen}
                  onClick={() => setProcessedAssetsViewOpen(false)}
                >
                  {t("campaign_display_tab_original_assets")}
                </MenuItem>

                <MenuItem
                  active={processedAssetsViewOpen}
                  onClick={() => setProcessedAssetsViewOpen(true)}
                >
                  {t("campaign_display_tab_processed_assets")}
                </MenuItem>
              </Menu>
            )}
            {/* Warning messages for contributors */}
            {!isAdmin && (
              <>
                {isCampaignExpired && (
                  <Message warning>{t("delivery_form_campaign_ended")}</Message>
                )}
                {!isCampaignActive && (
                  <Message warning>{t("delivery_form_campaign_inactive")}</Message>
                )}
              </>
            )}
            {!processedAssetsViewOpen ? (
              <CampaignDisplayOriginalAssets
                activeUser={activeUser}
                assets={assets}
                originalAssets={originalAssets}
                processedAssets={processedAssets}
                campaign={campaign}
                isCampaignExpired={isCampaignExpired}
                isCampaignActive={isCampaignActive}
                isAdmin={isAdmin}
                isDownloadingAssets={isDownloadingAssets}
                setEditingAssets={setEditingAssets}
                downloadAssets={downloadAssets}
                selectedAssets={selectedAssets}
                printServiceOptions={serviceOptions("PRINT_SERVICE")}
                setSelectedPrintServiceID={setSelectedPrintServiceID}
                isSubmitting={isSubmitting}
                onAssetSelected={onAssetSelected}
                setSelectedAssets={setSelectedAssets}
                selectedPrintServiceID={selectedPrintServiceID}
                submitSendAssets={submitSendAssets}
                agencys={agencys.items}
                users={users}
                isOwner={isOwner}
                currentAssets={currentAssets}
                stageProcessedAssets={stageProcessedAssets}
                isProcessingAssets={isProcessingAssets}
                t={t}
              />
            ) : (
              <CampaignDisplayProcessedAssets
                assets={processedAssetsList}
                processedAssets={processedAssets}
                campaign={campaign}
                isDownloadingAssets={isDownloadingAssets}
                digitalServiceOptions={serviceOptions("DIGITAL_SERVICE")}
                downloadAssets={downloadAssets}
                currentAssets={currentAssets}
                isPolling={isPolling}
                setProcessedAssetsViewOpen={setProcessedAssetsViewOpen}
                isSubmitting={isSubmitting}
                submitDigitalAssets={submitDigitalAssets}
                t={t}
              />
            )}
            <ProcessConfirmationModal
              data={confirmationModalData}
              open={confirmationModalOpen}
              setOpen={setConfirmationModalOpen}
              onSuccess={sendAssetsToProcessing}
              dataForOnSuccess={confirmationModalDataForOnSuccess}
              t={t}
            />
          </>
        ) : (
          <Message warning>{t("campaign_does_not_exist_message")}</Message>
        )}
      </LoadResolver>
    );

  if (assetActions)
    return (
      <DeliveryUpload
        actions={assetActions}
        onSuccess={() => setEditingCampaign(false)}
        campaign={campaign}
        isAdmin={isAdmin}
      />
    );

  if (editingCampaign)
    return (
      <CampaignForm
        campaign={campaign}
        assets={assets}
        onSuccess={() => setEditingCampaign(false)}
        onSuccessRunQC={onDeliverySuccess}
        goPrev={() => setEditingCampaign(false)}
      />
    );

  if (editingAssets)
    return (
      <DeliveryForm
        campaign={campaign}
        assets={assets}
        processedAssets={processedAssets}
        onSuccess={onDeliverySuccess}
        goPrev={() => setEditingAssets(false)}
        dispatch={dispatch}
        isAdmin={isAdmin}
        deliveredAssetResolutions={deliveredAssetResolutions}
        deliveredImageAssetResolutions={deliveredImageAssetResolutions}
      />
    );
};

export default CampaignDisplay;
